import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaFileCsv, FaDownload, FaTrash, FaSync, FaHistory } from 'react-icons/fa';
import Swal from 'sweetalert2';
import '../../styles/pages/RRHH/DTPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Página del módulo DT (Dirección del Trabajo)
 * Permite generar y gestionar archivos CSV para reportes laborales
 */
const DTPage = ({ token, id_company }) => {
    // Estados
    const [tiposReporte, setTiposReporte] = useState([]);
    const [historial, setHistorial] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generando, setGenerando] = useState(false);

    // Filtros
    const [filtroAnio, setFiltroAnio] = useState(new Date().getFullYear());
    const [filtroMes, setFiltroMes] = useState('');

    // Formulario de generación
    const [formData, setFormData] = useState({
        tipo_reporte: '',
        anio: new Date().getFullYear(),
        mes: new Date().getMonth() + 1,
        parametros: {}
    });

    const fetchTiposReporte = useCallback(async () => {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/rrhh/dt/tipos-reporte`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success) {
                setTiposReporte(response.data.data || []);
            }
        } catch (error) {
            console.error('Error al obtener tipos de reporte:', error);
        }
    }, [token]);

    const fetchHistorial = useCallback(async () => {
        try {
            setLoading(true);
            const params = { id_company };
            if (filtroAnio) params.anio = filtroAnio;
            if (filtroMes) params.mes = filtroMes;

            const response = await axios.get(
                `${API_BASE_URL}/api/rrhh/dt/historial`,
                {
                    params,
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            if (response.data.success) {
                setHistorial(response.data.data || []);
            }
        } catch (error) {
            console.error('Error al obtener historial:', error);
        } finally {
            setLoading(false);
        }
    }, [filtroAnio, filtroMes, id_company, token]);

    useEffect(() => {
        fetchTiposReporte();
    }, [fetchTiposReporte]);

    useEffect(() => {
        fetchHistorial();
    }, [fetchHistorial]);

    /**
     * Genera un nuevo archivo CSV
     */
    const handleGenerarCSV = async () => {
        if (!formData.tipo_reporte) {
            Swal.fire('Error', 'Debe seleccionar un tipo de reporte', 'error');
            return;
        }

        try {
            setGenerando(true);
            const response = await axios.post(
                `${API_BASE_URL}/api/rrhh/dt/generar-csv`,
                {
                    id_company,
                    ...formData
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                Swal.fire('Éxito', 'Archivo CSV generado correctamente', 'success');
                fetchHistorial();
            } else {
                Swal.fire('Error', response.data.message || 'Error al generar CSV', 'error');
            }
        } catch (error) {
            console.error('Error al generar CSV:', error);
            Swal.fire('Error', error.response?.data?.message || 'Error al generar CSV', 'error');
        } finally {
            setGenerando(false);
        }
    };

    /**
     * Descarga un archivo CSV
     */
    const handleDescargar = async (id, nombreArchivo) => {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/rrhh/dt/descargar/${id}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', nombreArchivo || 'reporte.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error al descargar:', error);
            Swal.fire('Error', 'Error al descargar el archivo', 'error');
        }
    };

    /**
     * Elimina un registro del historial
     */
    const handleEliminar = async (id) => {
        const result = await Swal.fire({
            title: '¿Está seguro?',
            text: 'Esta acción eliminará el registro del historial',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await axios.delete(
                    `${API_BASE_URL}/api/rrhh/dt/historial/${id}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                Swal.fire('Eliminado', 'Registro eliminado correctamente', 'success');
                fetchHistorial();
            } catch (error) {
                console.error('Error al eliminar:', error);
                Swal.fire('Error', 'Error al eliminar el registro', 'error');
            }
        }
    };

    /**
     * Genera lista de años para el selector
     */
    const getAnios = () => {
        return [2026, 2025, 2024];
    };

    /**
     * Genera lista de meses para el selector
     */
    const meses = [
        { value: 1, label: 'Enero' },
        { value: 2, label: 'Febrero' },
        { value: 3, label: 'Marzo' },
        { value: 4, label: 'Abril' },
        { value: 5, label: 'Mayo' },
        { value: 6, label: 'Junio' },
        { value: 7, label: 'Julio' },
        { value: 8, label: 'Agosto' },
        { value: 9, label: 'Septiembre' },
        { value: 10, label: 'Octubre' },
        { value: 11, label: 'Noviembre' },
        { value: 12, label: 'Diciembre' }
    ];

    return (
        <div className="dt-container">
            {/* Header */}
            <div className="dt-header">
                <h1><FaFileCsv /> DT - Generación de Archivos CSV</h1>
                <div className="header-buttons">
                    <button
                        className="btn-refresh"
                        onClick={fetchHistorial}
                        disabled={loading}
                    >
                        <FaSync className={loading ? 'spinning' : ''} /> Actualizar
                    </button>
                </div>
            </div>

            {/* Sección de Generación */}
            <div className="dt-section">
                <h2>Generar Nuevo Archivo</h2>
                <div className="dt-form-grid">
                    <div className="form-group">
                        <label>Tipo de Reporte</label>
                        <select
                            value={formData.tipo_reporte}
                            onChange={(e) => setFormData({ ...formData, tipo_reporte: e.target.value })}
                        >
                            <option value="">Seleccione un tipo...</option>
                            {tiposReporte.map((tipo) => (
                                <option key={tipo.codigo} value={tipo.codigo}>
                                    {tipo.nombre}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Año</label>
                        <select
                            value={formData.anio}
                            onChange={(e) => setFormData({ ...formData, anio: parseInt(e.target.value) })}
                        >
                            {getAnios().map((anio) => (
                                <option key={anio} value={anio}>{anio}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Mes</label>
                        <select
                            value={formData.mes}
                            onChange={(e) => setFormData({ ...formData, mes: parseInt(e.target.value) })}
                        >
                            {meses.map((mes) => (
                                <option key={mes.value} value={mes.value}>{mes.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group form-group-button">
                        <button
                            className="btn-generar"
                            onClick={handleGenerarCSV}
                            disabled={generando || !formData.tipo_reporte}
                        >
                            {generando ? (
                                <><FaSync className="spinning" /> Generando...</>
                            ) : (
                                <><FaFileCsv /> Generar CSV</>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Filtros del Historial */}
            <div className="dt-filters">
                <h2><FaHistory /> Historial de Archivos Generados</h2>
                <div className="filters-row">
                    <div className="filter-group">
                        <label>Año</label>
                        <select
                            value={filtroAnio}
                            onChange={(e) => setFiltroAnio(e.target.value)}
                        >
                            <option value="">Todos</option>
                            {getAnios().map((anio) => (
                                <option key={anio} value={anio}>{anio}</option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-group">
                        <label>Mes</label>
                        <select
                            value={filtroMes}
                            onChange={(e) => setFiltroMes(e.target.value)}
                        >
                            <option value="">Todos</option>
                            {meses.map((mes) => (
                                <option key={mes.value} value={mes.value}>{mes.label}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Tabla de Historial */}
            <div className="dt-table-container">
                {loading ? (
                    <div className="loading-container">
                        <FaSync className="spinning" />
                        <p>Cargando historial...</p>
                    </div>
                ) : historial.length === 0 ? (
                    <div className="empty-state">
                        <FaFileCsv />
                        <p>No hay archivos generados</p>
                        <span>Los archivos CSV que generes aparecerán aquí</span>
                    </div>
                ) : (
                    <table className="dt-table">
                        <thead>
                            <tr>
                                <th>Tipo de Reporte</th>
                                <th>Período</th>
                                <th>Fecha Generación</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {historial.map((item) => (
                                <tr key={item.id}>
                                    <td>{item.tipo_reporte}</td>
                                    <td>{item.mes}/{item.anio}</td>
                                    <td>{new Date(item.fecha_generacion).toLocaleString('es-CL', { timeZone: 'America/Santiago' })}</td>
                                    <td>
                                        <span className={`estado-badge ${item.estado?.toLowerCase()}`}>
                                            {item.estado || 'Generado'}
                                        </span>
                                    </td>
                                    <td className="actions-cell">
                                        <button
                                            className="btn-action btn-download"
                                            onClick={() => handleDescargar(item.id, item.nombre_archivo)}
                                            title="Descargar"
                                        >
                                            <FaDownload />
                                        </button>
                                        <button
                                            className="btn-action btn-delete"
                                            onClick={() => handleEliminar(item.id)}
                                            title="Eliminar"
                                        >
                                            <FaTrash />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default DTPage;
